import {
	ContractDamage,
	ContractHazard,
	ContractSuretyType,
	defaultAttachmentTypeLabelMap,
	IndemnityAgreementSignatureMethod,
	SuretyBondClosedReason,
	type SuretyBondFormType,
	SuretyBondIssuanceMethod,
	type SuretyBondType,
} from "@inrev/common";
import { ReactElement, type ReactNode } from "react";
import { HiOutlineMail } from "react-icons/hi";
import accelerantLogoSm1_5x from "./assets/logos/accelerant-logo-icon-25h@1.5x.png";
import {
	default as accelerantLogoSm,
	default as accelerantLogoSm2x,
} from "./assets/logos/accelerant-logo-icon-25h@2x.png";
import lnicLogoSm1_5x from "./assets/logos/lnic-logo-icon-25h@1.5x.png";
import lnicLogoSm2x from "./assets/logos/lnic-logo-icon-25h@2x.png";
import lnicLogoSm from "./assets/logos/lnic-logo-sm@2x.png";
import type { BondFormData } from "./types";

export const ADMIN_BASE_URL = "/v2/admin";
export const AGENT_BASE_URL = "/v2";

export const accountAttachmentTypeLabelMap: typeof defaultAttachmentTypeLabelMap =
	defaultAttachmentTypeLabelMap;

export enum USStateOrTerritoryName {
	AL = "Alabama",
	AK = "Alaska",
	AZ = "Arizona",
	AR = "Arkansas",
	CA = "California",
	CO = "Colorado",
	CT = "Connecticut",
	DE = "Delaware",
	DC = "Washington DC",
	FL = "Florida",
	GA = "Georgia",
	HI = "Hawaii",
	ID = "Idaho",
	IL = "Illinois",
	IN = "Indiana",
	IA = "Iowa",
	KS = "Kansas",
	KY = "Kentucky",
	LA = "Louisiana",
	ME = "Maine",
	MD = "Maryland",
	MA = "Massachusetts",
	MI = "Michigan",
	MN = "Minnesota",
	MS = "Mississippi",
	MO = "Missouri",
	MT = "Montana",
	NE = "Nebraska",
	NV = "Nevada",
	NH = "New Hampshire",
	NJ = "New Jersey",
	NM = "New Mexico",
	NY = "New York",
	NC = "North Carolina",
	ND = "North Dakota",
	OH = "Ohio",
	OK = "Oklahoma",
	OR = "Oregon",
	PA = "Pennsylvania",
	RI = "Rhode Island",
	SC = "South Carolina",
	SD = "South Dakota",
	TN = "Tennessee",
	TX = "Texas",
	UT = "Utah",
	VT = "Vermont",
	VA = "Virginia",
	WA = "Washington",
	WV = "West Virginia",
	WI = "Wisconsin",
	WY = "Wyoming",
}

export const usStateOrTerritoryOptions = (
	Object.keys(USStateOrTerritoryName) as (keyof typeof USStateOrTerritoryName)[]
).map((stateKey) => ({ value: stateKey, label: USStateOrTerritoryName[stateKey] }));

export const carrierLogoSmallMap: Record<string, ReactElement<HTMLImageElement>> = {
	"lexington-national-for-accelerant": (
		<img
			src={lnicLogoSm}
			srcSet={`${lnicLogoSm1_5x} 1.5x, ${lnicLogoSm2x} 2x`}
			width={20}
			height={20}
		/>
	),
	accelerant: (
		<img
			src={accelerantLogoSm}
			srcSet={`${accelerantLogoSm1_5x} 1.5x, ${accelerantLogoSm2x} 2x`}
			width={23}
		/>
	),
};

export const exposureSizeOptions = [
	{ value: "small", label: "Up to $500k" },
	{ value: "medium", label: "$500k - $1M" },
	{ value: "large", label: "More than $1M" },
];

export const bondRequestIssuanceMethodTypeLabelMap: Record<SuretyBondIssuanceMethod, string> = {
	agent: "Agent Issue",
	electronic: "Electronic copy",
	physical: "inRev Issue: Wet Signatures & Raised Seals",
};

export const agentUserRoleTypeLabelMap: Record<string, string> = {
	"agent-admin": "Agent Admin",
	agent: "Agent",
};

export const contractDamageOptions = [
	{
		value: ContractDamage.consequential,
		label: "Consequential Damages",
		tooltip:
			"Consequential Damages are indirect costs incurred by the Obligee as a result of the contractor’s delay to the project<br/><br/>Examples are: lost revenue, lost profit, etc.",
	},
	{ value: ContractDamage.prime_contract, label: "Prime Contract Damages" },
	{
		value: ContractDamage.liquidated_gt_5000,
		label: "Liquidated damages greater than $5,000",
		tooltip:
			"Liquidated Damages are the daily penalties the contractor must pay as a result of causing a delay to the project",
	},
	{ value: ContractDamage.other, label: "Other damages" },
	{ value: ContractDamage.none, label: "None of the above" },
];

export const contractHazardOptions = [
	{
		value: ContractHazard.design_build,
		label: "Design-build",
		tooltip:
			"Design Build occurs when the Principal is responsible for creating the project specs and stamping the design drawings",
	},
	{
		value: ContractHazard.environmental_or_remediation,
		label: "Environmental or remediation work",
	},
	{ value: ContractHazard.superfund, label: "Work at a superfund site" },
	{
		value: ContractHazard.efficiency_guarantees,
		label: "Efficiency guarantees",
		tooltip:
			"Efficiency Guarantees are present when the Principal is responsible for specific system performance<br/><br/>Examples are: uptime, reliability, output, energy usage/output, etc.)",
	},
	{ value: ContractHazard.sovereign_nation_owner, label: "Work for a sovereign nation owner" },
	{ value: ContractHazard.renewable, label: "Renewable contract" },
	{ value: ContractHazard.takeover, label: "Takeover of an existing contract" },
	{ value: ContractHazard.none, label: "None of the above" },
];

export const suretyBondClosedReasonTypeLabelMap: Record<SuretyBondClosedReason, string> = {
	bid_lost: "Bid lost",
	bid_won: "Bid won",
	expired: "Term expired",
	input_error: "Input error",
	non_payment: "Premium non-payment",
	not_awarded: "Project not awarded",
	other: "Other",
	project_completed: "Project completed",
	replaced: "Replaced by another bond",
	returned_by_obligee: "Returned by obligee",
};

export const bondRequestTypeLabelMap: Record<ContractSuretyType, string> = {
	bid: "Bid",
	bid_to_final: "Bid to Final",
	final: "Final",
};

export const bondTypeLabelMap: Record<SuretyBondType, string> = {
	bid: "Bid",
	final: "Final",
	subdivision: "Subdivision",
	maintenance: "Maintenance",
};

export const bondFormTypeLabelMap: Record<SuretyBondFormType, string> = {
	bid: "Bid",
	pnp: "Performance & Payment",
	performance: "Performance",
	payment: "Payment",
	supply: "Supply",
};

export const EMPTY_BOND_FORM: BondFormData = { templateId: "", upload: { type: "", files: [] } };

export const PROJECT_NAME_TOOLTIP =
	"This will be referenced on the bond form and can be edited after submission";
export const SUBMITTED_PROJECT_NAME_TOOLTIP = "This will be referenced on the bond form";

export const finalBondFormTypeOptions = [
	{ value: "pnp", label: "Performance and Payment" },
	{ value: "performance", label: "Performance" },
	{ value: "payment", label: "Payment" },
];

export const indemnityAgreementSignatureMethodTypeDisplayMap: Record<
	IndemnityAgreementSignatureMethod,
	ReactNode
> = {
	electronic: (
		<div className="grid grid-cols-[auto_1fr] grid-rows-2 gap-x-[10px]">
			<HiOutlineMail className="self-center text-[16px]" />
			<span>
				{IndemnityAgreementSignatureMethod.label(IndemnityAgreementSignatureMethod.electronic)}
			</span>
			<span></span>
			<span className="text-[13px] text-secondary italic">Sent via Docusign</span>
		</div>
	),
	physical: "Wet Signatures",
};
